import React from 'react';
import { CardContent } from './ui/card';
import { Target, Eye, Zap, Shield, Users, Award } from 'lucide-react';
import SectionEyebrow from '@/components/SectionEyebrow';
import { FadeIn, Stagger, StaggerItem } from '@/components/motion-primitives';

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Misyonumuz',
      description:
        'Restoran, kafe, bar, pub gibi mekanların otomasyon alt yapısını ortak bir platforma taşıyarak işletme sahiplerinin işini kolaylaştırmak.',
    },
    {
      icon: Eye,
      title: 'Vizyonumuz',
      description:
        "Türkiye'nin en güvenilir ve en gelişmiş restoran otomasyon platformu olmak, sektörde standardı belirlemek.",
    },
    {
      icon: Zap,
      title: 'Neden Yapıyoruz',
      description:
        'Eski, hantal ve birbirinden farklı yapılardan bıktık. Modern, hızlı ve entegre çözümler sunmak istiyoruz.',
    },
  ];

  const features = [
    {
      icon: Shield,
      title: 'Açık fiyatlandırma',
      description:
        'Paket ve entegrasyon ücretlerini ayrı ayrı gösteriyoruz; dönem toplamını karar vermeden önce görebilirsiniz.',
    },
    {
      icon: Users,
      title: 'Birlikte tanıtım',
      description: 'Demo ortamında masa, sipariş ve adisyon ekranlarını birlikte inceleyip sorularınızı yanıtlıyoruz.',
    },
    {
      icon: Award,
      title: 'Kurulum ve eğitim',
      description: 'Tilki kurulumu için ek ücret talep etmiyoruz. Eğitim ve danışmanlık desteği ana pakete dahil.',
    },
  ];

  return (
    <section id="about" className="relative section-y-lg bg-surface overflow-hidden">
      <div className="brand-glow w-[480px] h-[480px] -top-32 left-1/2 -translate-x-1/2" aria-hidden="true" />
      <div className="absolute inset-0 bg-grid-pattern opacity-50" aria-hidden="true" />

      <div className="container relative mx-auto px-4">
        {/* Header */}
        <FadeIn className="text-center mb-16">
          <SectionEyebrow className="mb-4">Hakkımızda</SectionEyebrow>
          <h2 className="type-h2 text-balance mb-4">
            Tilki <span className="text-gradient">Otomasyon</span>
          </h2>
          <p className="type-body-lg text-pretty max-w-2xl mx-auto">
            Tilki Otomasyon’u restoran ve kafelerin masa, sipariş ve adisyon işlemlerini tek yerde yönetebilmesi için
            geliştiriyoruz. Ürünü tanıtırken ekranları ve kullanım akışını birlikte inceliyoruz.
          </p>
        </FadeIn>

        <div className="mb-12 rounded-2xl border border-hairline bg-surface-2 p-6 sm:p-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-brand-700">İşletmeniz için tek platform</p>
          <h2 className="mt-3 font-poppins text-2xl font-semibold text-ink">
            Siparişten sadakate, bulut tabanlı yönetim
          </h2>
          <p className="mt-4 max-w-2xl leading-relaxed text-ink-muted">
            Masa, adisyon, QR menü ve sadakat yönetimini tek platformda bir araya getiriyoruz. Tanıtımda kullanım
            akışını birlikte inceleyelim. Pakete dahil kurulum ve eğitim desteğiyle işletmenizin başlangıç sürecinde
            yanınızdayız.
          </p>
          <a
            href="mailto:iletisim@tilkiotomasyon.com"
            className="mt-4 inline-block break-all font-medium text-brand-700 underline underline-offset-4"
          >
            iletisim@tilkiotomasyon.com
          </a>
        </div>
        {/* Values Section */}
        <Stagger className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-20" stagger={0.08}>
          {values.map((value, index) => (
            <StaggerItem
              key={index}
              className="text-center rounded-2xl border border-hairline bg-surface shadow-card hover:shadow-card-lift hover:border-brand-200 transition-all duration-300"
            >
              <CardContent className="p-8">
                <div className="mx-auto w-12 h-12 bg-brand-50 text-brand-700 rounded-xl flex items-center justify-center mb-5">
                  <value.icon className="w-6 h-6" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-ink mb-3 font-poppins">{value.title}</h3>
                <p className="text-ink-muted leading-relaxed font-inter">{value.description}</p>
              </CardContent>
            </StaggerItem>
          ))}
        </Stagger>

        {/* How We Do It Section */}
        <FadeIn className="mb-20">
          <div className="relative overflow-hidden rounded-3xl border border-hairline bg-surface-2 p-8 lg:p-14">
            <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-12 items-center">
              <div>
                <h3 className="font-poppins font-bold text-ink text-3xl lg:text-4xl tracking-tight leading-[1.1] mb-5 text-balance">
                  Nasıl <span className="text-gradient">Yapıyoruz</span>
                </h3>
                <p className="type-body-lg text-pretty mb-6">
                  Tilki Otomasyon adı altında geliştirdiğimiz platform sayesinde, restoran işletmeciliğinin tüm
                  süreçlerini dijitalleştiriyoruz.
                </p>
                <ul className="space-y-3">
                  {[
                    'Modern teknolojiler kullanıyoruz',
                    'Kullanıcı deneyimini ön planda tutuyoruz',
                    'Sürekli geliştirme ve güncelleme yapıyoruz',
                  ].map((line) => (
                    <li key={line} className="flex items-center space-x-3 text-ink font-inter">
                      <span className="w-1.5 h-1.5 bg-brand-500 rounded-full flex-shrink-0" aria-hidden="true" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col items-center justify-center text-center">
                <img src="/favicon.svg" alt="" width="80" height="80" className="w-20 h-20 mb-4" />
                <p className="text-ink-muted italic font-inter max-w-xs">
                  "İşletmenizi dijital dünyaya taşımanın en kolay yolu"
                </p>
              </div>
            </div>
          </div>
        </FadeIn>

        {/* Features Grid */}
        <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-8" stagger={0.07}>
          {features.map((feature, index) => (
            <StaggerItem key={index} className="text-center">
              <div className="mx-auto w-12 h-12 bg-brand-50 text-brand-700 rounded-xl flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6" aria-hidden="true" />
              </div>
              <h4 className="text-lg font-semibold text-ink mb-2 font-poppins">{feature.title}</h4>
              <p className="text-ink-muted text-sm font-inter">{feature.description}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
};

export default About;
